import {
  EmptyState,
  Tabs,
  ErrorBanner,
  TextField,
} from "frontend-kit/components/index.js";
import { listMarkets, loadBackendConfig } from "./gateway-api.js";
import { luxToDusk, isContractOwner } from "./chain-config.js";
import { duskAmountNode } from "./dusk-amount.js";
import { getChainApp, getBlockHeight, getWallet } from "./chain-app.js";
import { pollCached } from "./data-cache.js";
import {
  effectiveStatus,
  normalizeStatus,
  isLiveStatus,
  isResolvedStatus,
  STATUS_ORDER,
} from "./market-status.js";
import {
  onChainCategoryLabel,
  onChainCategoryStyle,
} from "./category-colors.js";
import { renderWenEventCard } from "./wen-event-card.js";

/** Slow tier per docs/prediction-market/prediction-market-portal-fixes-plan.md
 * §3a - an event list is fine to lag a bit behind chain state. */
const BROWSE_POLL_MS = 20000;

const STATUS_LABELS = {
  open: "Open",
  pending_yes: "Pending YES",
  pending_no: "Pending NO",
  under_review: "Council action",
  finalized: "Finalized",
  cancelled: "Cancelled",
};

function volumeNode(lux) {
  const frag = document.createDocumentFragment();
  frag.append(duskAmountNode(lux), " vol");
  return frag;
}

const ONE_DUSK_LUX = 1_000_000_000n;

/** Format pool size for card meta (compact, no "Pool" prefix). */
function poolLabelFromLux(poolLux) {
  if (poolLux == null) return null;
  const dusk = Number(poolLux) / 1e9;
  if (!Number.isFinite(dusk)) return null;
  if (dusk >= 1000) return `${dusk.toLocaleString(undefined, { maximumFractionDigits: 0 })} DUSK`;
  if (dusk >= 1) return `${dusk.toLocaleString(undefined, { maximumFractionDigits: 2 })} DUSK`;
  return `${dusk.toLocaleString(undefined, { maximumFractionDigits: 4 })} DUSK`;
}

function isThinPool(poolLux, initialLiquidity) {
  if (poolLux == null) return false;
  try {
    const seed = BigInt(initialLiquidity ?? 0);
    const floor = seed > ONE_DUSK_LUX ? seed : ONE_DUSK_LUX;
    const threshold = (floor * 25n) / 100n;
    return BigInt(poolLux) < threshold;
  } catch {
    return false;
  }
}

/** Spot YES % from FPMM reserves (no / (yes+no)). */
function yesPercentFromReserves(info) {
  if (!info) return null;
  const yes = Number(info.yes_reserve);
  const no = Number(info.no_reserve);
  const sum = yes + no;
  if (!sum || Number.isNaN(sum)) return null;
  return Math.round((no / sum) * 100);
}

export async function renderBrowsePage({ onOpen, log, registerCleanup }) {
  const root = document.createElement("div");
  root.className = "browse-page";

  const heading = document.createElement("h2");
  heading.className = "browse-title";
  heading.textContent = "Events";
  root.append(heading);

  const toolbar = document.createElement("div");
  toolbar.className = "browse-toolbar";
  root.append(toolbar);

  const grid = document.createElement("div");
  grid.className = "market-grid wen-event-grid";
  root.append(grid);

  let markets = [];
  let yesPctById = {};
  let poolLuxById = {};
  let filter = "live";
  /** @type {"all" | number} */
  let category = "all";
  // Must default to "all" - pairing Live with status "open" hides every
  // live-but-not-open market (pending_yes / pending_no / under_review).
  let statusFilter = "all";
  let search = "";
  let err = null;
  let blockHeight = null;
  let loading = true;
  // paint() fully rebuilds the toolbar, including the search TextField -
  // fine for the first load, but a poll tick doing that while the user is
  // mid-search would blow away input focus/cursor position. Only the
  // first load gets the full toolbar repaint (categories/status chips
  // included); poll-triggered reloads repaint just the grid. Missing a
  // brand-new category/status chip until the page is revisited is a
  // reasonable trade for never disrupting an active search.
  let hasPaintedOnce = false;

  async function load() {
    yesPctById = {};
    try {
      const cfg = loadBackendConfig();
      const owner = isContractOwner(getWallet());
      markets = await listMarkets(cfg, { includeHidden: owner });
      err = null;
      log?.(`loaded ${markets.length} markets from gateway`);
    } catch (e) {
      const msg = e.message || String(e);
      log?.(`browse error: ${msg}`);
      // A background poll tick failing shouldn't blank out (or error-
      // banner over) whatever's already showing - stale-while-revalidate
      // means keep serving the last good list. Only treat this as a
      // page-level error before there's ever been a successful load.
      if (hasPaintedOnce) {
        let stale = toolbar.querySelector(".browse-stale");
        if (!stale) {
          stale = document.createElement("p");
          stale.className = "hint browse-stale";
          toolbar.prepend(stale);
        }
        stale.textContent = "Gateway refresh failed - showing last good list.";
        return;
      }
      err = msg;
      markets = [];
    }
    // Paint immediately - do not block the page on chain reads.
    loading = false;
    if (hasPaintedOnce) {
      const stale = toolbar.querySelector(".browse-stale");
      stale?.remove();
      paintGridOnly();
    } else {
      paint();
      hasPaintedOnce = true;
    }

    if (!markets.length) return;
    // Enrich YES% + effective status in the background; never block first paint.
    void (async () => {
      try {
        const dusk = await getChainApp();
        try {
          blockHeight = await getBlockHeight();
        } catch {
          blockHeight = null;
        }
        await Promise.all(
          markets.map(async (m) => {
            try {
              const info = await dusk.readContract({
                contract: "predictionMarket",
                functionName: "market_info",
                args: m.market_id,
              });
              const pct = yesPercentFromReserves(info);
              if (pct != null) yesPctById[m.market_id] = pct;
              const yesR = info?.yes_reserve;
              const noR = info?.no_reserve;
              if (yesR != null && noR != null) {
                poolLuxById[m.market_id] = BigInt(yesR) + BigInt(noR);
              }
              m.close_block = info?.close_block ?? m.close_block ?? null;
              if (info?.status != null) m.on_chain_status = info.status;
              if (info?.initial_liquidity != null) {
                m.initial_liquidity = info.initial_liquidity;
              }
              m.status = effectiveStatus(m.on_chain_status ?? m.status, {
                closeBlock: m.close_block,
                blockHeight,
              });
            } catch {
              /* leave gateway status / -% */
            }
          })
        );
        paintGridOnly();
      } catch {
        /* wallet/node not ready - cards still render */
      }
    })();
  }

  function marketCategoryId(m) {
    if (m.market_category == null) return null;
    const n = Number(m.market_category);
    return Number.isInteger(n) && n >= 0 ? n : null;
  }

  function categories() {
    const set = new Set();
    for (const m of markets) {
      const id = marketCategoryId(m);
      if (id != null) set.add(id);
    }
    return [...set].sort((a, b) => a - b);
  }

  function statuses() {
    const set = new Set();
    for (const m of markets) set.add(normalizeStatus(m.status));
    return STATUS_ORDER.filter((s) => set.has(s));
  }

  function paint() {
    toolbar.replaceChildren();
    grid.replaceChildren();
    if (err) toolbar.append(ErrorBanner({ message: err }));

    toolbar.append(
      Tabs({
        tabs: [
          { id: "live", label: "Live" },
          { id: "resolved", label: "Resolved" },
          { id: "all", label: "All" },
        ],
        activeId: filter,
        onChange: (id) => {
          filter = id;
          paint();
        },
      })
    );

    const searchWrap = document.createElement("div");
    searchWrap.className = "browse-search";
    const searchField = TextField({
      id: "browse-search",
      label: "Search",
      value: search,
      onInput: (v) => {
        search = v;
        paintGridOnly();
      },
    });
    const searchInput = searchField.querySelector("input");
    if (searchInput) {
      searchInput.classList.add("search-pill");
      searchInput.placeholder = "Search events";
    }
    searchWrap.append(searchField);
    toolbar.append(searchWrap);

    const cats = categories();
    if (cats.length) {
      const chips = document.createElement("div");
      chips.className = "category-chips";
      const allChip = document.createElement("button");
      allChip.type = "button";
      allChip.className = "dt-pill wen-cat-chip";
      allChip.textContent = "All";
      allChip.classList.toggle("is-active", category === "all");
      allChip.addEventListener("click", () => {
        category = "all";
        paint();
      });
      chips.append(allChip);
      cats.forEach((id) => {
        const label = onChainCategoryLabel(id);
        const style = onChainCategoryStyle(id);
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "dt-pill wen-cat-chip";
        chip.style.background = style.bg;
        chip.style.color = style.fg;
        chip.textContent = label ?? String(id);
        chip.classList.toggle("is-active", category === id);
        chip.addEventListener("click", () => {
          category = id;
          paint();
        });
        chips.append(chip);
      });
      toolbar.append(chips);
    }

    const sts = statuses();
    if (sts.length) {
      const statusChips = document.createElement("div");
      statusChips.className = "category-chips";
      const allChip = document.createElement("button");
      allChip.type = "button";
      allChip.className = "dt-pill wen-cat-chip";
      allChip.textContent = "Any status";
      allChip.classList.toggle("is-active", statusFilter === "all");
      allChip.addEventListener("click", () => {
        statusFilter = "all";
        paint();
      });
      statusChips.append(allChip);
      sts.forEach((s) => {
        const chip = document.createElement("button");
        chip.type = "button";
        chip.className = "dt-pill wen-cat-chip";
        chip.textContent = STATUS_LABELS[s] ?? s;
        chip.classList.toggle("is-active", statusFilter === s);
        chip.addEventListener("click", () => {
          statusFilter = s;
          paint();
        });
        statusChips.append(chip);
      });
      toolbar.append(statusChips);
    }

    paintGridOnly();
  }

  function paintSkeleton() {
    grid.replaceChildren();
    for (let i = 0; i < 4; i++) {
      const sk = document.createElement("div");
      sk.className = "dt-card wen-skel-card";
      sk.innerHTML =
        '<div class="skel" style="width:56px;height:16px;margin-bottom:12px"></div>' +
        '<div class="skel" style="width:92%;height:13px;margin-bottom:8px"></div>' +
        '<div class="skel" style="width:65%;height:13px;margin-bottom:14px"></div>' +
        '<div class="skel" style="width:46px;height:26px;margin-bottom:10px"></div>' +
        '<div class="skel" style="width:100%;height:6px;margin-bottom:10px"></div>' +
        '<div class="skel" style="width:70%;height:10px"></div>';
      grid.append(sk);
    }
  }

  function paintGridOnly() {
    if (loading && !markets.length) {
      paintSkeleton();
      return;
    }
    grid.replaceChildren();
    const q = search.trim().toLowerCase();
    const filtered = markets.filter((m) => {
      if (filter === "live") {
        if (!isLiveStatus(m.status)) return false;
      } else if (filter === "resolved") {
        if (!isResolvedStatus(m.status)) return false;
      }
      if (statusFilter !== "all" && normalizeStatus(m.status) !== statusFilter) return false;
      if (category !== "all" && marketCategoryId(m) !== category) return false;
      if (q) {
        const title = (m.metadata?.title ?? `Event #${m.market_id}`).toLowerCase();
        const cat = (onChainCategoryLabel(marketCategoryId(m)) ?? "").toLowerCase();
        const desc = (m.metadata?.description ?? "").toLowerCase();
        if (!title.includes(q) && !desc.includes(q) && !cat.includes(q)) return false;
      }
      return true;
    });

    if (!filtered.length) {
      const empty = document.createElement("div");
      empty.className = "dt-card wen-empty-card";
      empty.textContent = q
        ? `No events match "${search.trim()}".`
        : "No events. Try another filter, or create one.";
      grid.append(empty);
      return;
    }

    const wrap = document.createDocumentFragment();
    for (const m of filtered) {
      const title = m.metadata?.title ?? `Event #${m.market_id}`;
      const poolLux = poolLuxById[m.market_id] ?? null;
      const seed = m.initial_liquidity ?? null;
      wrap.append(
        renderWenEventCard({
          id: m.market_id,
          title,
          category: onChainCategoryLabel(marketCategoryId(m)),
          marketCategory: marketCategoryId(m),
          status: m.status,
          yesPercent: yesPctById[m.market_id] ?? null,
          poolLabel: poolLabelFromLux(poolLux),
          thinPool: isThinPool(poolLux, seed),
          tradeCount: m.trade_count ?? null,
          winningOutcome: m.winning_outcome,
          unverified: m.metadata?.verified === false,
          hidden: m.hidden === true,
          onClick: onOpen,
        })
      );
    }
    grid.append(wrap);
  }

  // Show skeleton until first gateway payload arrives.
  paintSkeleton();

  // First paint awaits pollCached's immediate delivery (rather than also
  // calling load() directly), so mounting the page doesn't double-fetch -
  // one cycle serves both "first paint" and "the poll's initial tick".
  // `fresh: true` is required: load() mutates this closure's `markets`, so a
  // warm cache hit (Events → detail → back within 20s) must still re-run it.
  await new Promise((resolve) => {
    let settled = false;
    const done = () => {
      if (settled) return;
      settled = true;
      resolve();
    };
    const unsubscribe = pollCached("pm-browse-markets", load, {
      intervalMs: BROWSE_POLL_MS,
      fresh: true,
      onUpdate: () => done(),
    });
    registerCleanup?.(() => {
      unsubscribe();
      // If a newer renderApp tore us down before the first tick, don't leave
      // main.js awaiting forever on "Loading…".
      done();
    });
  });
  return root;
}
